import { axiosInstance } from "@/lib/axiosInstance";

// ─── Types ────────────────────────────────────────────────────────────────────

/** The five values the backend enforces via DB CHECK constraint + API regex. */
export type InterviewMode =
  | "phone"
  | "video"
  | "in_person"
  | "technical"
  | "hr";

export type InterviewStatus =
  | "scheduled"
  | "completed"
  | "cancelled"
  | "no_show";

export interface InterviewType {
  id: string;
  application_id: string;
  student_id: string;
  /** Widened to string so an unrecognised value humanises instead of blanking. */
  interview_type: InterviewMode | (string & {});
  /** ISO-8601 UTC. Always parse via parseUtc() — never `new Date()` directly. */
  scheduled_at: string;
  duration_minutes: number;
  /** Free text from the recruiter — never trusted as a URL. See safeMeetingLink(). */
  meeting_link: string | null;
  venue: string | null;
  status: InterviewStatus | (string & {});
  internship_id: string;
  /** Snapshot pushed at schedule time. Null only for rows created before the backfill. */
  internship_title: string | null;
  company_name: string | null;
  rescheduled_at: string | null;
  reschedule_count: number;
  created_at: string;
  updated_at: string;
}

export interface InterviewListParams {
  /**
   * Server-side filter: status=scheduled AND scheduled_at >= now()
   * AND the parent application is not withdrawn/rejected.
   */
  upcoming?: boolean;
}

// ─── Error helpers ────────────────────────────────────────────────────────────

/**
 * Every student-service error shares one shape:
 *   { success: false, error: { code, message } }
 * The message is not user-safe — VALIDATION_ERROR carries a raw Python repr —
 * so callers map the code to their own copy instead of rendering `message`.
 */
interface ApiErrorShape {
  response?: {
    status?: number;
    data?: { error?: { code?: string; message?: string } };
  };
}

export const getApiErrorCode = (error: unknown): string | undefined =>
  (error as ApiErrorShape)?.response?.data?.error?.code;

export const getApiErrorStatus = (error: unknown): number | undefined =>
  (error as ApiErrorShape)?.response?.status;

export const isNotFoundError = (error: unknown): boolean => {
  const code = getApiErrorCode(error);
  return (
    code === "INTERVIEW_NOT_FOUND" ||
    code === "APPLICATION_NOT_FOUND" ||
    getApiErrorStatus(error) === 404
  );
};

// ─── Service Functions ────────────────────────────────────────────────────────

/** Unwraps the `{ success, data }` envelope. Interview lists are a bare array. */
const unwrap = (raw: unknown) =>
  raw && typeof raw === "object" && "data" in (raw as object)
    ? (raw as { data: unknown }).data
    : raw;

const asArray = (raw: unknown): InterviewType[] => {
  const data = unwrap(raw);
  return Array.isArray(data) ? (data as InterviewType[]) : [];
};

export const fetchInterviews = async (
  params?: InterviewListParams
): Promise<InterviewType[]> => {
  const res = await axiosInstance.get("/v1/interviews", { params });
  return asArray(res.data);
};

export const fetchInterviewById = async (
  interviewId: string
): Promise<InterviewType> => {
  const res = await axiosInstance.get(`/v1/interviews/${interviewId}`);
  return unwrap(res.data) as InterviewType;
};

export const fetchInterviewsByApplication = async (
  applicationId: string
): Promise<InterviewType[]> => {
  const res = await axiosInstance.get(
    `/v1/applications/${applicationId}/interviews`
  );
  return asArray(res.data);
};
